/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { ProxyDataService } from '@app/core/http';
import { Logger, MessageService } from '@core/lib/services';
import { appConstants } from '@app/common/app-constants';

export class SseClientCacheFactory {
  private readonly cache: Map<number, any>;
  private listeners: any = {};

  constructor(
    private name: string,
    private endpoint: (params) => string,
    private comparator,
    private log: Logger,
    private dataService: ProxyDataService,
    private messageService: MessageService
  ) {
    this.cache = new Map<number, any>();
  }

  getCache() {
    return this.cache;
  }

  get(patientId, params, isStreaming, force) {
    const cachedData = this.cache.get(patientId);
    const requestedProcedureId = params.primaryExamId;
    if (cachedData && !force) {
      // If cache is available start the SSE call and send the cached data.
      if (!isStreaming) {
        return this.dataServiceFunction(this.endpoint(params), requestedProcedureId);
      } else {
        return Promise.resolve(cachedData);
      }
    }
    return this.dataServiceFunction(this.endpoint(params), requestedProcedureId);
  }

  updateCache(id, serverData) {
    this.cache.set(id, serverData);
  }

  remove(id) {
    this.cache.delete(id);
  }

  on(event, listener, context) {
    if (!_.isFunction(listener)) {
      this.log.error('ssl-client-cache-factory - .on() - listener is not a function');
      return;
    }

    listener._context = context || null;

    if (this.listeners[event]) {
      this.listeners[event].push(listener);
    } else {
      this.listeners[event] = [listener];
    }

    return {
      unregister: () => {
        const index = this.listeners[event].indexOf(listener);
        this.listeners[event].splice(index, 1);
      }
    };
  }

  fireEvent(eventName, patientId, serverData = undefined, cachedData = undefined) {
    const eventListeners = this.listeners[eventName];
    if (!eventListeners) {
      this.log.error('client-cache-factory: .fireEvent() - no listeners have been registered, please check eventName');
    } else {
      _.each(eventListeners, listener => {
        listener.apply(listener._context, [patientId, serverData, cachedData]);
      });
    }
  }

  compareAndUpdateCache(id, data, event) {
    this.correctRequestedProcedureId(data);
    const cachedData = this.cache.get(id);
    if (cachedData && cachedData.patientComparisons === data.patientComparisons) {
      this.comparator(id, cachedData, data);
    } else {
      this.cache.set(id, data);
      if (event) {
        this.fireEvent(event, id, data);
      }
    }
  }

  updateSingleFieldInCache(data) {
    const cachedData = this.cache.get(data.requestedProcedureId)
      ? this.cache.get(data.requestedProcedureId).requestedProcedure
      : void 0;
    if (!cachedData) {
      return;
    } else {
      _.extend(cachedData, _.pick(data, _.keys(cachedData)));
    }
  }

  getComparator() {
    return this.comparator;
  }

  private correctRequestedProcedureId(data) {
    if (data && data.patientComparisons) {
      _.each(data.patientComparisons, comparison => {
        comparison.requestedProcedureId = [
          appConstants.repositoryType.XED,
          appConstants.repositoryType.GLOBAL
        ].includes(comparison.repositoryType)
          ? comparison.procedureStudyInstanceUID
          : parseInt(comparison.procedureStudyInstanceUID, 10);
      });
    }
  }

  private dataServiceFunction(url, id) {
    return new Promise((resolve, reject) => {
      this.dataService.get(url).then(
        () => {
          this.log.info('Patient comparisons SSE call invoked successfully for exam id:' + id);
          resolve();
        },
        reason => {
          this.log.error('Error occurred while getting patient comparisons, ERROR -', JSON.stringify(reason));
          this.messageService.broadcast('refreshFailed', id);
          reject(reason);
        }
      );
    });
  }
}
